/** Normalize legacy membership group names to starter / plus / pro. */
export function normalizeMemberGroup(group) {
  const g = String(group || 'starter').toLowerCase().trim();
  if (g === 'default' || g === '' || g === 'free') return 'starter';
  if (g === 'vip') return 'plus';
  if (g === 'svip' || g === 'premium') return 'pro';
  return g;
}

/** Display label for membership tier. */
export function displayMemberGroup(group) {
  switch (normalizeMemberGroup(group)) {
    case 'starter':
      return 'Starter';
    case 'plus':
      return 'Plus';
    case 'pro':
      return 'Pro';
    default:
      return group || 'Starter';
  }
}

function parseModels(models) {
  if (!models) return [];
  if (Array.isArray(models)) {
    return models.map((m) => String(m).trim()).filter(Boolean);
  }
  if (typeof models === 'string') {
    return models
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** First model id from a token's models field (for NextChat default). */
export function pickDefaultModel(models) {
  const list = parseModels(models);
  return list.length ? list[0] : '';
}

/**
 * Resolve the single model for Chat/API. Legacy tokens may still store comma-separated lists.
 * @returns {{ model: string, models: string[], multiLegacy: boolean }}
 */
export function resolveTokenModel(models) {
  const list = parseModels(models);
  return {
    model: list.length ? list[0] : '',
    models: list,
    multiLegacy: list.length > 1,
  };
}

/** Console server address without trailing slash or /v1 (for NextChat, OAuth, Chat links). */
export function normalizeServerAddress(url) {
  let s = String(url || '').trim();
  if (!s) return '';
  // NextChat appends "v1/chat/completions" — strip any trailing /v1 (including user typos).
  while (/\/v1\/?$/i.test(s)) {
    s = s.replace(/\/v1\/?$/i, '');
  }
  return s.replace(/\/$/, '');
}

function resolveServerAddress() {
  let serverAddress = '';
  try {
    const status = JSON.parse(localStorage.getItem('status') || '{}');
    serverAddress = status.server_address || '';
  } catch {
    serverAddress = '';
  }
  serverAddress = normalizeServerAddress(serverAddress);
  if (!serverAddress) {
    serverAddress = normalizeServerAddress(window.location.origin);
  }
  return serverAddress;
}

function resolveChatLink() {
  return localStorage.getItem('chat_link') || 'https://app.nextchat.dev';
}

/** OpenAI-compatible base URL for NextChat (no trailing /v1 — NextChat appends /v1/chat/completions itself). */
export function normalizeApiBaseForNextChat(serverAddress) {
  return normalizeServerAddress(serverAddress || resolveServerAddress());
}

function formatApiKey(key) {
  const k = String(key || '').trim();
  if (!k) return '';
  return k.startsWith('sk-') ? k : `sk-${k}`;
}

/**
 * Route model through OpenAI-compatible provider in NextChat.
 * Prevents names like Doubao-* / gpt-* from binding to built-in vendor channels.
 */
export function encodeModelForOpenAIProvider(model) {
  const m = String(model || '').trim();
  if (!m) return m;
  if (/@OpenAI$/i.test(m)) return m;
  if (/@/.test(m)) return m;
  return `${m}@OpenAI`;
}

/** Strip @Provider suffix for session modelConfig.model field. */
export function bareModelName(modelWithProvider) {
  const m = String(modelWithProvider || '').trim();
  if (!m) return m;
  const idx = m.lastIndexOf('@');
  return idx > 0 ? m.slice(0, idx) : m;
}

/** NextChat CUSTOM_MODELS string: hide defaults, expose token models on OpenAI relay. */
export function buildCustomModelsForNextChat(models) {
  const list = parseModels(models);
  if (!list.length) return '';
  return `-all,${list.map((m) => `+${encodeModelForOpenAIProvider(m)}`).join(',')}`;
}

/**
 * Pick default model: prefer explicit token scope, else first from live /v1/models list.
 */
export function resolveDefaultModel(preferredModel, modelIds) {
  const list = parseModels(modelIds);
  const preferred = String(preferredModel || '').trim();
  if (preferred) {
    const bare = bareModelName(preferred);
    const hit = list.find((id) => id === preferred || id === bare);
    if (hit) return encodeModelForOpenAIProvider(hit);
  }
  if (list.length) return encodeModelForOpenAIProvider(list[0]);
  return preferred ? encodeModelForOpenAIProvider(preferred) : '';
}

function base64UrlEncode(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Stable window name: one browser tab per token (reuse on repeat Chat clicks). */
export function chatWindowName(tokenId) {
  const id =
    tokenId !== undefined && tokenId !== null && String(tokenId).trim() !== ''
      ? String(tokenId)
      : 'default';
  return `rp-chat-${id}`;
}

/** Open Chat in a named window so the same token reuses one tab. */
export function openNextChatWindow(url, tokenId) {
  return window.open(url, chatWindowName(tokenId));
}

/**
 * Minimal payload for rp-launch.html on chat host.
 * Bridge page fetches /v1/models with the token key (source of truth for new channels/models).
 */
export function buildNextChatLaunchPayload({
  key,
  models,
  serverAddress,
  tokenId,
  tokenName,
  attachTier,
} = {}) {
  const apiBase = normalizeApiBaseForNextChat(serverAddress);
  const { model, multiLegacy } = resolveTokenModel(models);
  const modelHints = model ? [model] : [];
  return {
    key: formatApiKey(key),
    url: apiBase,
    preferredModel: model,
    models: modelHints,
    multiLegacy,
    attachTier: attachTier || 'text',
    tokenId: tokenId != null ? String(tokenId) : '',
    tokenName: String(tokenName || '').trim(),
  };
}

/**
 * Build NextChat URL via same-origin launch page (mount deploy/nextchat/rp-launch.html on chat host).
 */
export function buildNextChatLaunchUrl({
  key,
  models,
  chatLink,
  serverAddress,
  tokenId,
  tokenName,
  attachTier,
} = {}) {
  const base = (chatLink || resolveChatLink()).replace(/\/$/, '');
  const payload = buildNextChatLaunchPayload({
    key,
    models,
    serverAddress,
    tokenId,
    tokenName,
    attachTier,
  });
  const hash = base64UrlEncode(JSON.stringify(payload));
  return `${base}/rp-launch.html#${hash}`;
}

/** Build NextChat URL with token-scoped models (uses rp-launch.html on chat host). */
export function buildNextChatUrl({
  key,
  models,
  chatLink,
  serverAddress,
  tokenId,
  tokenName,
  attachTier,
} = {}) {
  return buildNextChatLaunchUrl({
    key,
    models,
    chatLink,
    serverAddress,
    tokenId,
    tokenName,
    attachTier,
  });
}

/** Build LobeChat settings URL (baseURL includes /v1). */
export function buildLobeChatUrl({ key, chatLink, serverAddress } = {}) {
  const base = (chatLink || resolveChatLink()).replace(/\/$/, '');
  const apiBase = `${(serverAddress || resolveServerAddress()).replace(/\/$/, '')}/v1`;
  const settings = {
    keyVaults: {
      openai: {
        apiKey: formatApiKey(key),
        baseURL: apiBase,
      },
    },
  };
  return `${base}/?settings=${encodeURIComponent(JSON.stringify(settings))}`;
}

/** Map API preset payload (supports legacy default/vip/svip keys). */
export function normalizeScopePresets(data) {
  if (!data) {
    return { starter: [], plus: [], pro: [] };
  }
  const starter = data.starter || data.free || data.default || [];
  return {
    starter,
    free: starter,
    plus: data.plus || data.vip || [],
    pro: data.pro || data.svip || [],
  };
}

/** Normalize preset tier id from API or legacy UI values. */
export function normalizeScopeTier(tier) {
  const t = String(tier || 'starter').toLowerCase();
  if (t === 'default' || t === 'free') return 'starter';
  if (t === 'vip') return 'plus';
  if (t === 'svip' || t === 'premium') return 'pro';
  if (t === 'starter' || t === 'plus' || t === 'pro') return t;
  return 'custom';
}
